import PropTypes from 'prop-types';
import ResponseError from './ResponseError';
import { settings } from '../../config/settings';
import { getCSRFToken as defaultGetCSRFToken } from '../csrf';

/**
 * Helper function to setup request headers
 * @param {Object} Settings
 *  @property {String} contentType
 *  @property {String} acceptType
 *  @property {String} acceptLanguage
 *  @property {String} contentLanguage
 *  @property {String} version
 * @returns {Object}
 */
export const createRequestHeaders = ({
  contentType = 'application/json',
  acceptType = 'application/json',
  acceptLanguage = null,
  contentLanguage = null,
  version = settings?.bevyAPIVersion || 'bevy.1.0',
  getCSRFToken = defaultGetCSRFToken, // allows overriding the function for getting a CSRFToken
} = {}) => {
  const headers = {
    'X-CSRFToken': getCSRFToken(),
    // eslint-disable-next-line i18next/no-literal-string
    'X-Requested-With': 'XMLHttpRequest',
  };

  if (acceptLanguage) {
    headers['Accept-Language'] = acceptLanguage;
    // eslint-disable-next-line no-undef
  } else if (typeof Globals === 'object' && Globals.language_code) {
    // eslint-disable-next-line no-undef
    headers['Accept-Language'] = Globals.language_code;
  }

  if (contentType) headers['Content-Type'] = contentType;
  if (acceptType) headers.Accept = `${acceptType}; version=${version}`;

  if (contentLanguage) headers['Content-Language'] = contentLanguage;

  return headers;
};

createRequestHeaders.propTypes = {
  contentType: PropTypes.string,
  acceptType: PropTypes.string,
  acceptLanguage: PropTypes.string,
  contentLanguage: PropTypes.string,
  version: PropTypes.string,
};

/**
 * Default response handler, checks for JSON / Text and returns the response object
 * @param {Object} response fetch response object
 * @param {Boolean} ignoreNetworkError whether to ignore network errors
 * @returns {Object}
 */
export const createResponseHandler = async response => {
  const responseType = response?.headers?.get('content-type') || '';
  const isJSON = responseType?.includes('application/json');
  try {
    // Try to process the response as JSON, if it fails, process as text.
    // If this fails, the error will be caught and handled by the error handler.
    const data = await (isJSON ? response?.json() : response?.text());
    return {
      ...response,
      data,
    };
  } catch (error) {
    throw new ResponseError(error?.name, error?.message, response);
  }
};

createResponseHandler.propTypes = {
  response: PropTypes.object,
};

export const processBody = (body, convertBodyTo) => {
  return convertBodyTo === 'json' ? JSON.stringify(body) : body;
};

/**
 * Fetch handler, takes an api endpoint, fetch params, and throwError boolean.
 * Wrap this in a try / catch block to catch errors.
 *
 * This function is a simple wrapper, and should be composed with the needed
 * response and error handlers.
 *
 * @param {String} api endpoint /api/endpoint
 * @param {Object} fetchParams method, body, etc
 * @param {Boolean} throwOnError whether to ignore network errors
 * @param {Function} responseCallback function to handle response
 * @returns {Object} fetched data or thrown error
 */
export const fetchHandler = async ({
  api = '',
  processBodyAs = 'json',
  handleProcessBody = processBody,
  fetchParams = { method: 'GET' },
  throwOnError = true,
}) => {
  try {
    const params = { ...fetchParams };
    if (fetchParams.body) {
      params.body = handleProcessBody(fetchParams.body, processBodyAs);
    }
    const response = await fetch(api, {
      ...params,
      credentials: 'same-origin',
    });

    // If the user is not authenticated, reload the page to force a login.
    if (response.status === 401 || response.status === 403) {
      window.location.reload();
    }

    // Allow the consuming wrapper to handle the response,
    // even if it's not okay and status is less than 400 (non-network replated error)
    if (response.ok || response.status < 400) return response;

    // If response not ok, error is likely 4xx-5xx.
    // Fetch API does not throw errors when response is not ok.
    // Throw error so it can be caught and handled.
    if (throwOnError) {
      // Throw the response, which causes the promise to reject.
      // Let the consuming wrapper handle it.
      // We use a custom Error Type to handle throwing the response as an Error.
      // This will allow consuming APIs like react-query to interpret it as an Error.
      let body;
      try {
        body = await response.json();
      } catch (error) {
        body = null;
      }
      throw new ResponseError('ResponseError', response?.statusText, response, body);
    }
  } catch (error) {
    if (throwOnError) {
      // Pass along any ResponseErrors thrown above,
      if (error.name === 'ResponseError') {
        throw error;
      }
      // If the error is not a ResponseError, throw a new ResponseError.
      throw new ResponseError(error?.name, error?.message, error);
    }
  }
  return undefined;
};

fetchHandler.propTypes = {
  api: PropTypes.string.isRequired,
  fetchParams: PropTypes.object,
  throwOnError: PropTypes.bool,
};
